import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from 'src/app/material.module';
import { MatPaginator } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, Observable, startWith, map, debounceTime } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MY_FORMATS } from 'src/app/common/dateFormat';
import { DATE_FORMAT } from 'src/app/common/constant/constants.type';
import { BillType } from 'src/app/common/enums/bill-type';
import { AppointmentStatusType } from 'src/app/common/enums/appointment-status-type';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { LookupService } from 'src/app/services/lookup.service';
import { LoginService } from 'src/app/login.service';
import { HospitalStateService } from 'src/app/services/hospitalstate.service';
import { IHospitalInfo } from '../../hospital/ihospital-info';
import { environment } from 'src/environments/environment';
import { ReportsService } from '../reports.service';
import { IBillingReportItem, IPagedBillingReportResult } from './ibilling-report';
import { AddBillChargeComponent } from '../../dialogs/bill/bill.component';
import { BillDialogData } from '../../dialogs/bill/bill-dialog-data.interface';
import { Appointment } from '../../appointment/iappointment';
import { IPDBillData } from '../../dialogs/bill/ipd-bill-data.interface';
import { BaseRespone, PaginationResult } from 'src/app/models/common.response';
import { ReportColumn, ReportPrintService } from '../../../services/report-print.service';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-billing-report',
  standalone: true,
  imports: [CommonModule, MaterialModule, ReactiveFormsModule, TranslateModule, TablerIconsModule],
  providers: [
    DatePipe,
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './billing-report.component.html',
  styleUrls: ['./billing-report.component.scss'],
})
export class BillingReportComponent implements OnInit {
  filterForm: FormGroup;
  dataSource = new MatTableDataSource<IBillingReportItem>();
  displayedColumns = ['patientName', 'billNo', 'billAmount', 'paidAmount', 'pendingAmount', 'billDate', 'paymentStatus'];
  isLoading = false;
  maxDate = new Date();

  doctorsList: CommonDropDown[] = [];
  selectedDoctors: CommonDropDown[] = [];
  doctorControls = new FormControl('');
  filteredDoctorList$: Observable<CommonDropDown[]>;
  isDoctor = false;
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  patientSearchControl = new FormControl('');


  @ViewChild(MatPaginator) paginator: MatPaginator;

  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();
  private hospitalInfo: IHospitalInfo;
  private staticimagepath = environment.imagePath;
  pagination: PaginationResult;
  reportCount: number = 0;

  constructor(
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private http: HttpClient,
    private reportService: ReportsService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private lookupService: LookupService,
    private loginService: LoginService,
    private hospitalData: HospitalStateService,
    private printService: ReportPrintService,
  ) { }

  get startDateControl(): FormControl {
    return this.filterForm.get('startDate') as FormControl;
  }

  get endDateControl(): FormControl {
    return this.filterForm.get('endDate') as FormControl;
  }

  get doctorIdsControl(): FormControl {
    return this.filterForm.get('doctorIds') as FormControl;
  }

  ngOnInit(): void {
    this.hospitalInfo = this.hospitalData.getHospital();
    this.isDoctor = this.loginService.isDoctor();
    const now = new Date();
    this.filterForm = this.fb.group({
      startDate: [new Date(now.getFullYear(), now.getMonth(), 1)],
      endDate: [new Date()],
      doctorIds: [[]],
    });

    this.dataSource.filterPredicate = (data: IBillingReportItem, filter: string): boolean => {
      const search = filter?.toLowerCase() ?? '';
      const patientName = (data.patientName ?? '').toLowerCase();
      const billNo = (data.billNo ?? '').toString().toLowerCase();
      return patientName.includes(search) || billNo.includes(search);
    };

    this.loadDoctors();

    this.reportService.pagination$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
        }
      });

    this.reportService.billing$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (data) {
          this.dataSource.data = data || [];
          this.dataSource.paginator = this.paginator;
          this.reportCount = data?.length || 0;
        }
      });

    this.patientSearchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((value: string | null) => {
        const filterValue = (value ?? '').trim().toLowerCase();
        this.dataSource.filter = filterValue;
        if (this.dataSource.paginator) {
          this.dataSource.paginator.firstPage();
        }
      });

    this.loadReport();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  loadReport(): void {
    const { startDate, endDate, doctorIds } = this.filterForm.getRawValue();

    if (new Date(endDate) < new Date(startDate)) {
      this.toastr.error(this.translate.instant('DATE_COMPARE_START_END'));
      return;
    }

    this.isLoading = true;
    this.reportService.getBillingReport({
      startDate: this.datePipe.transform(startDate, DATE_FORMAT) ?? '',
      endDate: this.datePipe.transform(endDate, DATE_FORMAT) ?? '',
      doctorIds: doctorIds?.length > 0 ? doctorIds : undefined,
      pageNumber: 0,
      pageSize: 0
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: BaseRespone<IPagedBillingReportResult>) => {
          this.isLoading = false;
          if (!response.isSuccess) {
            this.toastr.error(response.message || 'Error fetching report');
          }
        },
        error: () => this.isLoading = false,
      });
  }

  clearFilters(): void {
    const now = new Date();
    if (!this.isDoctor) {
      this.selectedDoctors = [];
      this.doctorControls.setValue('', { emitEvent: false });
    }
    this.patientSearchControl.setValue('');
    this.dataSource.filter = '';
    this.filterForm.patchValue({
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date(),
      doctorIds: this.isDoctor ? this.filterForm.value.doctorIds : [],
    });
    this.loadReport();
  }

  async printReport(): Promise<void> {
    if (this.dataSource.data.length === 0) {
      this.toastr.error(this.translate.instant('LABEL.NO_DATA'));
      return;
    }

    const { startDate, endDate } = this.filterForm.getRawValue();
    const columns: ReportColumn[] = [
      { header: 'Patient Name', field: 'patientName', flex: '2' },
      { header: 'Bill No', field: 'billNo', flex: '1.5' },
      {
        header: 'Bill Amount', field: 'billAmount', flex: '1.5',
        formatter: (val) => val?.toFixed(2) ?? '0.00'
      },
      {
        header: 'Paid Amount', field: 'paidAmount', flex: '1.5',
        formatter: (val) => val?.toFixed(2) ?? '0.00'
      },
      {
        header: 'Pending', field: 'pendingAmount', flex: '1.5',
        formatter: (val) => val?.toFixed(2) ?? '0.00'
      },
      {
        header: 'Bill Date', field: 'billDate', flex: '1.5',
        formatter: (val) => this.datePipe.transform(val, 'dd-MM-yyyy') ?? ''
      },
      {
        header: 'Status', field: 'paymentStatus', flex: '1',
        formatter: (val) => this.getStatusLabel(val)
      },
    ];

    await this.printService.printReport({
      title: this.translate.instant('LABEL.BILLING_REPORT'),
      items: this.dataSource.data,
      columns: columns,
      startDate: startDate,
      endDate: endDate,
      doctorNames: this.selectedDoctors.map(d => d.name).join(', ')
    });
  }

  openBillDialog(row: IBillingReportItem): void {
    this.dialog.open(AddBillChargeComponent, {
      autoFocus: false,
      position: { top: '0', right: '0' },
      height: '100vh',
      width: '82vw',
      maxWidth: '1400px',
      panelClass: ['bill-dialog', 'side-panel'],
      data: this.buildDialogData(row),
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.loadReport();
        }
      });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Paid': return 'status-paid';
      case 'PartialPaid': return 'status-partial';
      case 'UnPaid': return 'status-unpaid';
      default: return '';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'Paid': return 'Paid';
      case 'PartialPaid': return 'Partial';
      case 'UnPaid': return 'Unpaid';
      default: return '';
    }
  }

  private loadDoctors(): void {
    if (this.isDoctor) {
      const doctorId = this.loginService.getUserId();
      this.filterForm.patchValue({ doctorIds: doctorId ? [doctorId] : [] });
      return;
    }

    this.lookupService.getDoctors()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.doctorsList = data ?? [];

        this.filteredDoctorList$ = this.doctorControls.valueChanges.pipe(
          startWith(''),
          debounceTime(300),
          map((value) => {
            const val = typeof value === 'string' ? value : '';
            return this._filterDoctorContent(val);
          })
        );

        // Sync selected doctors with FormGroup
        this.doctorControls.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            const selectedIds = this.selectedDoctors.map(d => d.id);
            this.filterForm.get('doctorIds')?.setValue(selectedIds, { emitEvent: false });
          });
      });
  }

  private _filterDoctorContent(value: string): CommonDropDown[] {
    const filterValue = value.toLowerCase();
    return this.doctorsList.filter(option =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  onDoctorToggle(doc: CommonDropDown): void {
    const index = this.selectedDoctors.findIndex(d => d.id === doc.id);

    if (index > -1) {
      this.selectedDoctors.splice(index, 1);
    } else {
      this.selectedDoctors.push(doc);
    }

    this.filterForm.get('doctorIds')?.setValue(this.selectedDoctors.map(d => d.id));

    this.doctorControls.setValue(
      this.selectedDoctors.map(d => d.name).join(', '),
      { emitEvent: false }
    );
  }

  isDoctorSelected(id: number): boolean {
    return this.selectedDoctors.some(d => d.id === id);
  }

  private buildDialogData(row: IBillingReportItem): BillDialogData {
    if (row.billType === 'IPDBill' && row.ipdId) {
      return this.buildIPDDialogData(row);
    }
    return this.buildOPDDialogData(row);
  }

  private buildIPDDialogData(row: IBillingReportItem): BillDialogData {
    return {
      billType: BillType.IPDBill,
      ipdBillData: {
        ipdId: row.ipdId,
        ipdNumber: row.ipdNumber ?? '',
        patientId: row.patientId,
        patientName: row.patientName,
        admissionDate: row.admissionDate ? new Date(row.admissionDate) : new Date(),
        dischargeDate: row.dischargeDate ? new Date(row.dischargeDate) : new Date(),
        roomChargeId: row.roomChargeId ?? 0,
        roomCharge: row.roomCharge ?? 0,
        roomTypeName: row.roomTypeName ?? '',
        wardName: '',
      } as IPDBillData,
      selectedDoctorId: row.doctorId,
    };
  }

  private buildOPDDialogData(row: IBillingReportItem): BillDialogData {
    return {
      billType: BillType.OPDBill,
      opdBillData: {
        opdId: row.opdId,
        billId: row.id,
        patientId: row.patientId,
        patientName: row.patientName,
        doctorId: row.doctorId,
        status: AppointmentStatusType.Attended,
        appointmentTypeId: row.appointmentTypeId,
        appointmentTypeName: row.appointmentTypeName,
        charge: row.charge,
        opdNumber: row.opdNumber,
        appointDate: row.appointDate,
      } as Appointment,
      selectedDoctorId: row.doctorId,
    };
  }

}
